import { makeStyles, Typography } from "@material-ui/core";
import { Autocomplete, Box, TextField } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ROWS_PER_PAGE } from "../App";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import usePositionList, { PositionStatus } from "../hooks/usePositionList";
import useWindowSize from "../hooks/useWindowSize";
import Position from "../models/Position";
import PositionService from "../services/PositionService";
import { BillingPolicy } from "../types/BillingPolicy";
import { RoleKey } from "../types/RoleKey";
import { Routes } from "../types/Routes";
import { ColorUtils } from "../utils/ColorUtils";
import { WorkingHours } from "../utils/enums/WorkingHours";
import OpsDateUtils from "../utils/OpsDateUtils";
import { SwitchCustom } from "../utils/SwitchCustom";
import useTableHeight from "../hooks/useTableHeight";
import { SelectComponent } from "../components/SelectComponent";
import PositionFormFields from "../components/EditionDialogs/Fields/PositionFormFields";
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";

import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";

import { useStyles as formStyles } from "../components/Main/formStyles";

export default function PositionList() {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const [selectedEditElement, setSelectedEditElement] = useState<Position | undefined>(undefined);

  const customStyles = formStyles();

  const {
    projectsNames,
    canManageProjects,
    update,
    selectedPosition,
    setSelectedPosition,
    newBillingPolicy,
    assigned,
    searchRoleKey,
    setSearchRoleKey,
    handleChangeAssignedProjects,
    handleOnChangeInputBillingPolicy,
    handleOnDeletePosition,
    onCreatePosition,
    onEditPosition,
    onUpdatePosition,
    setIsEditing,
    isEditing,
    hasWorklogs,
    isLoading,
    onCancelDialog,
    isShadowChecked,
    bonusValue,
    setIsShadowChecked,
    setBonusValue,
    userNames,
    buildBillingTypeText,
    searchParams,
    updateUrl,
    selectedProject,
    setSelectedProject,
    positionStatus,
    setPositionStatus,
    setStoredProject,
  } = usePositionList();

  const handleOnCheckShadow = (event: any) => {
    setIsShadowChecked(!isShadowChecked);
    setBonusValue(0);
  };

  const handleOnChangeBonus = (event: any) => {
    const newValue = parseInt(event.target.value);
    setBonusValue(newValue);
    setIsShadowChecked(false);
  };

  const onDataFetch = (pageNumber: number, count: number) => {
    return PositionService.findByProjectExternalIdAndRoleKey(
      pageNumber,
      count,
      selectedProject?.externalId ? selectedProject.externalId : "",
      searchRoleKey.label !== "All" ? searchRoleKey.label : "",
      positionStatus === PositionStatus.ACTIVE
    ).then((result) => result.getContent());
  };

  const renderAutoCompleteProjectName = (
    <SimpleAutoComplete
      label="Project name"
      items={projectsNames.length > 0 ? [{ label: "All", externalId: "ALL" }, ...projectsNames] : projectsNames}
      value={selectedProject ? selectedProject : { label: "", externalId: "" }}
      onChange={(newValue) => {
        setSelectedProject(newValue);
        searchParams.set("project", newValue.externalId);
        setStoredProject(newValue);
        updateUrl();
      }}
    />
  );
  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderAutoCompleteProjectName}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderAutoCompleteProjectName}
        {renderDialogFilters}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <Autocomplete
        disablePortal
        options={[
          {
            externalId: "",
            label: "All",
          },
          {
            externalId: RoleKey[RoleKey.SOFTWARE_ENGINEER],
            label: RoleKey.SOFTWARE_ENGINEER.toString(),
          },
          {
            externalId: RoleKey[RoleKey.TECH_LEAD],
            label: RoleKey.TECH_LEAD.toString(),
          },
          {
            externalId: RoleKey[RoleKey.QA_ENGINEER],
            label: RoleKey.QA_ENGINEER.toString(),
          },
          {
            externalId: RoleKey[RoleKey.PROJECT_MANAGER],
            label: RoleKey.PROJECT_MANAGER.toString(),
          },
        ]}
        getOptionLabel={(option) => option.label}
        sx={{ minWidth: 250, marginRight: "10px" }}
        size="small"
        value={searchRoleKey}
        onChange={(event, newValue) => {
          if (newValue) {
            setSearchRoleKey(newValue);
            searchParams.set("role", newValue.label);
            updateUrl();
          }
        }}
        renderInput={(params) => <TextField {...params} label="Role Key" variant="outlined" />}
      />
      <SelectComponent
        label={"Status"}
        value={positionStatus}
        setValue={setPositionStatus}
        options={Object.values(PositionStatus)}
        size="small"
      />
      <SwitchCustom checked={assigned} onChange={handleChangeAssignedProjects} size="medium" color="primary" label="Assigned to me" />
    </>
  );

  function handleOnCancel(): void {
    setIsEditing(false)
  }

  function getAdditionalActions(instance: Position): ActionsMenuEntryIcons[] {
    if (!canManageProjects) return (
      [{
        title: t("N/A"),
        action: () => {},
      }]
    )
    const options = [
      {
        title: t("Delete"),
        action: () => {
          setSelectedPosition(instance)
        },
        icon:<DeleteOutlineIcon/>
      },
      {
        title: t("Edit"),
        action: async () => {
          if (!canManageProjects) return undefined;
          await onEditPosition(instance);
          setIsEditing(true);
          setSelectedEditElement(instance);
        },
        icon:<EditIcon/>
      },
    ];

    return options;
  }

  return (
    <>
      <AppContainerFullHeight
        bottomSectionRef={bottomSectionRef}
        topSection={
          <>
            <AppToolbar title={t("Positions")}>{renderFilters()}</AppToolbar>
          </>
        }
        bottomSection={
          <Box sx={{ boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px" }}>
            <SpinnerWrapper isLoading={isLoading}>
              <TableBasedCrud<Position>
                headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
                buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
                cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
                dialogStyles={ customStyles.root }
                buttonContainer={ customButtonsContainer }
                gridButtonContainer={ gridButtonContainer }
                addButtonBuilder={ customButtonBuilder }
                maxHeight={calculateMaxHeight(canManageProjects && selectedProject?.externalId ? true : false)}
                stickyHeader
                stickyPagination
                elementName={t("Position")}
                rowsPerPage={ROWS_PER_PAGE}
                forceUpdateOn={update}
                enablePagination
                denseTable
                resetValue={{
                  internalHours: WorkingHours.DEFAULT,
                  externalHours: WorkingHours.DEFAULT,
                  externalRoleName: "",
                  internalRoleName: "",
                  roleKey: "",
                  billingPolicy: "",
                  monthlyMinimumHours: "176",
                  shortStartDate: new Date(),
                  shortEndDate: null,
                  bonus: 0,
                  shadow: null,
                  onDemand: null,
                }}
                onDataFetch={onDataFetch}
                onCreate={canManageProjects && selectedProject?.externalId ? onCreatePosition : undefined}
                onUpdate={canManageProjects ? onUpdatePosition : undefined}
                onClickEdit={async (instance) => {
                  if (!canManageProjects) return undefined;
                  await onEditPosition(instance);
                  setIsEditing(true);
                }}
                onCancelDialog={onCancelDialog}
                renderDialogContent={(register, errors, control, instance) => (
                  <PositionFormFields
                    register={register}
                    errors={errors}
                    control={control}
                    newBillingPolicy={newBillingPolicy}
                    onChangeBillingPolicy={handleOnChangeInputBillingPolicy}
                    disabledBonusHours={isShadowChecked}
                    onChangeBonus={handleOnChangeBonus}
                    readonlyShadowCheckbox={hasWorklogs || bonusValue > 0}
                    onCheckShadow={handleOnCheckShadow}
                    isEditing={isEditing}
                    userNames={userNames}
                  />
                )}
                tableColumns={[
                  {
                    header: t("External Role"),
                    content: (instance) => <>{instance.externalRoleName}</>,
                  },
                  {
                    header: t("Internal Role"),
                    content: (instance) => <>{instance.internalRoleName}</>,
                  },
                  {
                    header: t("Role Key"),
                    content: (instance) => <>{instance.roleKey}</>,
                  },
                  {
                    header: t("Internal Hours"),
                    content: (instance) => <>{instance.internalHours}</>,
                  },
                  {
                    header: t("External Hours"),
                    content: (instance) => <>{instance.externalHours}</>,
                  },
                  {
                    header: "Billing",
                    content: (instance) => <>{buildBillingTypeText(instance)}</>,
                  },
                  {
                    header: t("Minimum"),
                    content: (instance) => (
                      <>
                        {BillingPolicy[instance.billingPolicy] === BillingPolicy.SUM_HOURS_WITH_MINIMUM.toString()
                          ? instance.monthlyMinimumHours
                          : 0}
                      </>
                    ),
                  },
                  {
                    header: t("Start Date"),
                    content: (instance) => <>{OpsDateUtils.formatUTCAsDDMMMYYY(instance.shortStartDate)}</>,
                  },
                  {
                    header: t("End Date"),
                    content: (instance) => <>{instance.shortEndDate ? OpsDateUtils.formatUTCAsDDMMMYYY(instance.shortEndDate) : ""}</>,
                  },
                  {
                    header: t("Assigned To"),
                    content: (instance) => (
                      <>
                        <Link to={`${Routes.ASSIGNATIONS}?project=${selectedProject?.externalId}`}>{instance.assignedTo || ""}</Link>
                      </>
                    ),
                  },
                  {
                    header: t("Actions"),
                    content: (instance) => {
                      const actions = getAdditionalActions(instance)
                      return (<>
                        {actions.map((action) => {return (<ActionButton onClick={action.action} tooltip={action.title} icon={action.icon}/>)})}
                        </>
                      )
                    },
                  },
                ]}
                hideActions={true}  // Hide default Actions column
                buildAdditionalActions={(instance, refresh) => {
                  return [
                    {
                      title: t("Delete"),
                      action: () => setSelectedPosition(instance),
                    },
                  ];
                }}
                stylesForRowIfCondition={(instance) =>
                  instance.bonus > 0
                    ? { backgroundColor: ColorUtils.SUCCESS }
                    : instance.shadow
                    ? { backgroundColor: ColorUtils.BLUE }
                    : undefined
                }
              />
            </SpinnerWrapper>
          </Box>
        }
      />

      {/* Dialog to delete positions */}
      <AppDialog
        open={!!selectedPosition}
        onClose={() => setSelectedPosition(undefined)}
        title={"Delete"}
        content={
          <Typography>
            {`The next position is going to be eliminated: ${selectedPosition?.projectName} - ${selectedPosition?.externalRoleName}. 
            Do you want to confirm this action?`}
          </Typography>
        }
        buttons={[{ label: "Confirm delete", onClick: () => handleOnDeletePosition() }]}
      />
      {/* Dialog to manage filters  */}
      <FilterDialog title={"Positions"} setIsOpen={setIsOpen} isOpen={isOpen}>
        {renderDialogFilters}
      </FilterDialog>

      {/* Copy of the Dialog to Edit -- To use with Edit Action Icon */}
      <EditDialog<Position>
        headerStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom:-10, marginTop:10 }}
        buttonStyles={{ marginRight: 20, color: 'white', backgroundColor: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, marginBottom: 10 }}
        cancelButtonStyles={{ color: ColorUtils.ENSOLVERS_ORANGE_REBRANDING, border: `1px solid ${ColorUtils.ENSOLVERS_ORANGE_REBRANDING}`, marginBottom: 10 }}
        dialogStyles={ customStyles.root }     
        elementName={"Position"}
        dialogPosition={DialogPosition.Center}
        currentInstance={selectedEditElement}
        newItemDialogOpen={isEditing}
        onCreate={canManageProjects && selectedProject?.externalId ? onCreatePosition : undefined}
        onUpdate={canManageProjects ? onUpdatePosition : undefined}
        onCancel={handleOnCancel}
        renderDialogContent={(register, errors, control) => (
          <PositionFormFields
          register={register}
          errors={errors}
          control={control}
          newBillingPolicy={newBillingPolicy}
          onChangeBillingPolicy={handleOnChangeInputBillingPolicy}
          disabledBonusHours={isShadowChecked}
          onChangeBonus={handleOnChangeBonus}
          readonlyShadowCheckbox={hasWorklogs || bonusValue > 0}
          onCheckShadow={handleOnCheckShadow}
          isEditing={isEditing}
          userNames={userNames}
        />
        )}
        setNewInstanceDialogOpen={() => {}}
        setLastUpdateTimestamp={() => {}}
      />
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  desktopFilters: {
    columnGap: "1rem",
    display: "flex",
    flexDirection: "row",
    borderRadius: "4px",
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",
    "&> div:first-child": {
      minWidth: "150px",
      marginLeft: "0",
    },
    "&> div:nth-child(2)": {
      minWidth: "150px",
    },
    "& > div": {
      margin: "0 !important",
    },
  },
  mobileFilters: {
    "&> div:first-child": {
      minWidth: "150px",
      width: "100%",
      marginLeft: "0",
    },
    padding: "5px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  [theme.breakpoints.down(950)]: {
    desktopFilters: {
      gridRowGap: "1rem",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
    },
  },
}),
{ index: 1 }
);
